/**
 * Error Utilities - Unified error handling for the application
 * Provides user-friendly error messages and actionable guidance
 */

/**
 * Error type constants for categorizing errors
 */
export const ErrorType = {
  NETWORK: 'NETWORK_ERROR',
  PERMISSION: 'PERMISSION_ERROR',
  VALIDATION: 'VALIDATION_ERROR',
  AUTH: 'AUTH_ERROR',
  SERVER: 'SERVER_ERROR',
  UNKNOWN: 'UNKNOWN_ERROR',
} as const;

export type ErrorTypeValue = (typeof ErrorType)[keyof typeof ErrorType];

/**
 * Structured app error with user-friendly messaging
 */
export interface AppError {
  type: ErrorTypeValue;
  message: string;
  action: string;
  retryable: boolean;
  details?: unknown;
}

/**
 * Error message mapping - user-friendly messages for each error type
 */
export const ERROR_MESSAGES: Record<ErrorTypeValue, { message: string; action: string }> = {
  [ErrorType.NETWORK]: {
    message: 'Unable to connect. Please check your internet connection.',
    action: 'Try again or check your network settings.',
  },
  [ErrorType.PERMISSION]: {
    message: "You don't have permission to perform this action.",
    action: 'Contact your account Owner to request access.',
  },
  [ErrorType.VALIDATION]: {
    message: 'Please check your input and try again.',
    action: 'Review the highlighted fields and correct any errors.',
  },
  [ErrorType.AUTH]: {
    message: 'Your session has expired.',
    action: 'Please sign in again to continue.',
  },
  [ErrorType.SERVER]: {
    message: 'Something went wrong on our end.',
    action: 'Please try again in a few moments.',
  },
  [ErrorType.UNKNOWN]: {
    message: 'An unexpected error occurred.',
    action: 'Please try again or contact support if the problem persists.',
  },
};

/**
 * HTTP status code to error type mapping
 */
export const HTTP_STATUS_TO_ERROR_TYPE: Record<number, ErrorTypeValue> = {
  400: ErrorType.VALIDATION,
  401: ErrorType.AUTH,
  403: ErrorType.PERMISSION,
  404: ErrorType.VALIDATION, // Treat as validation since resource doesn't exist
  422: ErrorType.VALIDATION,
  429: ErrorType.SERVER, // Rate limited
  500: ErrorType.SERVER,
  502: ErrorType.SERVER,
  503: ErrorType.SERVER,
  504: ErrorType.NETWORK, // Gateway timeout often network-related
};

/**
 * Determine if an error type is retryable
 */
export function isRetryable(errorType: ErrorTypeValue): boolean {
  return errorType === ErrorType.NETWORK || errorType === ErrorType.SERVER;
}

/**
 * Get error type from HTTP status code
 */
export function getErrorTypeFromStatus(status: number): ErrorTypeValue {
  return HTTP_STATUS_TO_ERROR_TYPE[status] || ErrorType.UNKNOWN;
}

/**
 * Get user-friendly message for an error type
 */
export function getUserFriendlyMessage(errorType: ErrorTypeValue): string {
  return ERROR_MESSAGES[errorType]?.message || ERROR_MESSAGES[ErrorType.UNKNOWN].message;
}

/**
 * Get actionable guidance for an error type
 */
export function getActionableGuidance(errorType: ErrorTypeValue): string {
  return ERROR_MESSAGES[errorType]?.action || ERROR_MESSAGES[ErrorType.UNKNOWN].action;
}

/**
 * Create a structured AppError from various error sources
 */
export function createAppError(
  errorType: ErrorTypeValue,
  customMessage?: string,
  details?: unknown
): AppError {
  const base = ERROR_MESSAGES[errorType] || ERROR_MESSAGES[ErrorType.UNKNOWN];
  return {
    type: errorType,
    message: customMessage || base.message,
    action: base.action,
    retryable: isRetryable(errorType),
    details,
  };
}

/**
 * Get actionable message combining error description and recovery action
 * This is the primary function for displaying errors to users
 */
export function getActionableMessage(
  error: unknown,
  options?: {
    context?: string;
    httpStatus?: number;
    permissionNeeded?: string;
    fieldErrors?: Record<string, string>;
  }
): AppError {
  // Handle Axios errors
  if (isAxiosError(error)) {
    const status = error.response?.status;
    const serverMessage = extractServerErrorMessage(error.response?.data);
    const errorType = status ? getErrorTypeFromStatus(status) : ErrorType.NETWORK;

    // For permission errors, include specific permission info if available
    if (errorType === ErrorType.PERMISSION && options?.permissionNeeded) {
      return createAppError(
        errorType,
        `You need ${options.permissionNeeded} permissions to perform this action.`,
        error.response?.data
      );
    }

    // For validation errors, include server message if available
    if (errorType === ErrorType.VALIDATION && serverMessage) {
      return createAppError(errorType, serverMessage, error.response?.data);
    }

    // Network error (no response)
    if (!error.response) {
      return createAppError(ErrorType.NETWORK, undefined, error.message);
    }

    return createAppError(errorType, serverMessage, error.response?.data);
  }

  // Handle network/fetch errors
  if (error instanceof TypeError && error.message.toLowerCase().includes('network')) {
    return createAppError(ErrorType.NETWORK, undefined, error.message);
  }

  // Handle standard Error objects
  if (error instanceof Error) {
    // Check for specific error patterns
    if (error.message.toLowerCase().includes('network') || error.message.toLowerCase().includes('connection')) {
      return createAppError(ErrorType.NETWORK, undefined, error.message);
    }
    if (error.message.toLowerCase().includes('unauthorized') || error.message.toLowerCase().includes('authentication')) {
      return createAppError(ErrorType.AUTH, undefined, error.message);
    }
    if (error.message.toLowerCase().includes('permission') || error.message.toLowerCase().includes('forbidden')) {
      return createAppError(ErrorType.PERMISSION, undefined, error.message);
    }

    return createAppError(ErrorType.UNKNOWN, undefined, error.message);
  }

  // Handle HTTP status provided in options
  if (options?.httpStatus) {
    const errorType = getErrorTypeFromStatus(options.httpStatus);
    return createAppError(errorType);
  }

  // Handle field validation errors
  if (options?.fieldErrors && Object.keys(options.fieldErrors).length > 0) {
    const fieldCount = Object.keys(options.fieldErrors).length;
    const message =
      fieldCount === 1
        ? 'Please correct the error in the highlighted field.'
        : `Please correct the errors in ${fieldCount} fields.`;
    return createAppError(ErrorType.VALIDATION, message, options.fieldErrors);
  }

  // Fallback for unknown error types
  return createAppError(ErrorType.UNKNOWN);
}

/**
 * Type guard for Axios-like errors
 * Must have a response object OR be a network error with specific Axios structure
 */
function isAxiosError(error: unknown): error is {
  response?: { status?: number; data?: unknown };
  message?: string;
  code?: string;
  isAxiosError?: boolean;
} {
  if (typeof error !== 'object' || error === null) return false;

  // Explicitly marked as Axios error
  if ('isAxiosError' in error && error.isAxiosError === true) return true;

  // Has response object (typical HTTP error response)
  if ('response' in error && typeof (error as Record<string, unknown>).response === 'object') {
    return true;
  }

  // Has Axios-specific error code (ECONNABORTED, ERR_NETWORK, etc.)
  if ('code' in error && typeof (error as Record<string, unknown>).code === 'string') {
    const code = (error as Record<string, unknown>).code as string;
    if (code.startsWith('E') || code.startsWith('ERR_')) return true;
  }

  return false;
}

/**
 * Extract user-readable message from server error response
 * Handles various error response formats
 */
function extractServerErrorMessage(data: unknown): string | undefined {
  if (!data || typeof data !== 'object') return undefined;

  const errorData = data as Record<string, unknown>;

  // Standard format: { error: { message: "..." } }
  if (errorData.error && typeof errorData.error === 'object') {
    const error = errorData.error as Record<string, unknown>;
    if (typeof error.message === 'string') return error.message;
  }

  // Alternative formats
  if (typeof errorData.message === 'string') return errorData.message;
  if (typeof errorData.error === 'string') return errorData.error;

  return undefined;
}

/**
 * Get escalated error message after multiple retry failures
 */
export function getEscalatedMessage(errorType: ErrorTypeValue, retryCount: number): AppError {
  if (retryCount >= 3) {
    if (errorType === ErrorType.NETWORK) {
      return createAppError(
        ErrorType.NETWORK,
        'Still unable to connect after multiple attempts.',
        { retryCount }
      );
    }
    if (errorType === ErrorType.SERVER) {
      return createAppError(
        ErrorType.SERVER,
        "We're experiencing technical difficulties. Please try again later.",
        { retryCount }
      );
    }
  }
  return createAppError(errorType);
}

/**
 * Format permission error with specific role/permission needed
 */
export function formatPermissionError(
  action: string,
  requiredRole?: string
): AppError {
  const message = requiredRole
    ? `You need ${requiredRole} permissions to ${action}.`
    : `You don't have permission to ${action}.`;

  return createAppError(ErrorType.PERMISSION, message);
}
